<template>
  <div>
    <div>
      <div v-if="!loading">
        <table class="admin-table">
          <thead>
            <th>{{ !is_japan_locale ? $tr('common.list.name') : $tr('common.list.surname') }}</th>
            <th>{{ !is_japan_locale ? $tr('common.list.surname') : $tr('common.list.name') }}</th>
            <th>{{ $tr('common.list.email') }}</th>
            <th>{{ $tr('common.list.phone') }}</th>
            <th>{{ $tr('admin_console.company.settings.managers.mainManager') }}</th>
            <th></th>
          </thead>
          <tbody>
            <user-row
              v-for="manager in managers"
              :key="manager.id"
              :model="manager"
              :is_japan_locale="is_japan_locale"
              @set_main="set_main"
              @remove="remove"
            />
          </tbody>
        </table>

        <div class="standart-navigation">
          <div class="row-block actions">
            <div class="filters">
              <multiselect
                v-model="new_user"
                v-validate="'required'"
                class="big"
                type="text"
                :style="{ borderColor: errors.has('new_user') ? 'red' : '' }"
                :options="users_list"
                :show-labels="false"
                :show-no-results="false"
                :label="!is_japan_locale ? 'full_name' : 'full_name_reverse'"
                track-by="id"
                :placeholder="$tr('vacancy.preferences.accesses.placeholder')"
              />
            </div>
            <div class="actions-panel">
              <div class="buttons-group">
                <vbutton
                  label="common.add"
                  :disabled="!new_user || !new_user.id"
                  @click="add_user"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <vpreloader v-else />

      <transition name="fade">
        <modal-dialog
          :show-custom-buttons="false"
          :open="is_remove_modal_visible"
          type="warning"
          :text-body="$tr('admin_console.company.settings.managers.removeManager')"
          @close="toggle_remove_modal(false)"
        >
          <template slot="buttons">
            <div class="buttons-group buttons-group--modal">
              <vbutton
                label="common.cancel"
                color="transparent"
                @click="toggle_remove_modal(false)"
              />

              <vbutton label="common.ok" @click="remove_user" />
            </div>
          </template>
        </modal-dialog>
      </transition>
    </div>
  </div>
</template>

<script>
import { STORE_PREFIX } from '../../constants.js';
import { mapGetters } from 'vuex';
import { orderBy } from 'lodash';

import UserRow from './user_row';
import ModalDialog from '@/components/modals/ModalDialog';

export default {
  name: 'Managers',

  components: {
    UserRow,
    ModalDialog
  },

  props: ['companyId'],

  data() {
    return {
      manager_first: {},
      manager_second: {},
      new_user: {},
      is_remove_modal_visible: false,
      removing_user_id: null,
      is_get_manager: false
    };
  },

  computed: {
    ...mapGetters(STORE_PREFIX + 'admin', {
      loading: 'personal-managers/loading',
      company: 'company/one'
    }),

    is_japan_locale() {
      return this.$store.getters['locale/is_japan_locale'];
    },

    managers() {
      const list = this.$store.getters[STORE_PREFIX + 'admin/personal-managers/list'];

      if (!list || !list.length) {
        return [];
      }

      return Object.assign([], list).sort((a, b) => {
        return b.is_main - a.is_main;
      });
    },

    users_list() {
      let list = this.$store.getters[STORE_PREFIX + 'admin/users/get_list'] || [];
      if (!this.managers || !this.managers.length) {
        let sort_list = orderBy(
          list,
          [option => (option.first_name ? option.first_name.toLowerCase() : '')],
          ['first_name'],
          ['asc']
        );
        return sort_list;
      }

      let user_list = list.filter(user => {
        let is_exist = false;

        this.managers.forEach(manager => {
          if (manager.user_id === user.id) {
            is_exist = true;
          }
        });

        return !is_exist;
      });
      let sort_user = orderBy(
        user_list,
        [option => (option.first_name ? option.first_name.toLowerCase() : '')],
        ['first_name'],
        ['asc']
      );
      return sort_user;
    }
  },

  watch: {
    company: {
      handler: function (value) {
        if (!value) {
          return;
        }

        if (!this.is_get_manager) {
          this.get_managers();
          this.is_get_manager = true;
        }
      },

      immediate: true
    }
  },

  beforeMount() {
    this.get_all_users();
    this.get_managers();
  },

  beforeDestroy() {
    this.clear_list();
  },

  methods: {
    get_managers() {
      if (!this.company || !this.companyId) {
        return;
      }

      this.$store.dispatch(STORE_PREFIX + 'admin/personal-managers/get_list', {
        company_id: this.companyId
      });
    },

    get_all_users() {
      this.$store.dispatch(STORE_PREFIX + 'admin/users/fetch', {
        page: null,
        count: null
      });
    },

    remove(id) {
      this.removing_user_id = id;
      this.toggle_remove_modal(true);
    },

    set_main(model) {
      this.$store
        .dispatch(STORE_PREFIX + 'admin/personal-managers/update_list', [
          Object.assign({}, model, {
            is_main: true
          })
        ])
        .then(() => {
          this.get_managers();
        });
    },

    remove_user() {
      if (this.removing_user_id) {
        this.$store
          .dispatch(STORE_PREFIX + 'admin/personal-managers/remove_manager', {
            id: this.removing_user_id
          })
          .then(() => {
            this.get_managers();
            this.toggle_remove_modal(false);
            this.$noty.success(
              this.$tr('admin_console.company.settings.managers.removedManagerSuccess')
            );
          })
          .catch(err => this.$store.dispatch('global/setError', err));
      }
    },

    toggle_remove_modal(is_show) {
      this.is_remove_modal_visible = is_show;
    },

    add_user() {
      if (!this.new_user || !this.new_user.id) {
        return;
      }

      this.$store
        .dispatch(STORE_PREFIX + 'admin/personal-managers/create_manager', {
          company_id: this.companyId,
          user_id: this.new_user ? this.new_user.id : null
        })
        .then(() => {
          this.new_user = {};
          this.get_managers();
          this.$noty.success(
            this.$tr('admin_console.company.settings.managers.addedManagerSuccess')
          );
        })
        .catch(() => {
          this.$noty.error('Произошла ошибка');
        });
    },

    clear_list() {
      this.$store.commit(STORE_PREFIX + 'admin/personal-managers/clear_list');
    }
  }
};
</script>

<style lang="scss" scoped>
.admin-table {
  margin-bottom: 40px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;

  .vcv-button {
    margin-left: auto;
  }
}

.text-header {
  font-size: 26px;
  padding: 5px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.standart-navigation {
  display: inline-flex;
}
</style>
